import { RealtimeNegotiateApi } from '../../api/generated/apis/RealtimeNegotiateApi';
import { Configuration, ResponseError, type FetchAPI } from '../../api/generated/runtime';
import { AccessTokenProvider, AccessTokenUnavailableError } from './access-token.provider';
import type { RealtimeLifecycleResult } from './realtime-state.model';

export interface RealtimeGrant {
  readonly url: string;
  readonly accessToken: string;
  readonly expiresAtUtc: number;
  readonly clientCode: string;
  readonly hub: 'tiles';
  readonly schemaVersion: '1.0';
}

export abstract class RealtimeGrantClient {
  abstract negotiate(
    expectedClientCode: string,
    signal: AbortSignal,
  ): Promise<RealtimeLifecycleResult<RealtimeGrant>>;
}

export class GeneratedRealtimeGrantClient extends RealtimeGrantClient {
  private readonly basePath: string;

  constructor(
    signalRUrl: string,
    private readonly accessTokenProvider: AccessTokenProvider,
    private readonly now: () => number = Date.now,
    private readonly fetchApi: FetchAPI = fetch,
  ) {
    super();
    this.basePath = operationBasePath(signalRUrl);
  }

  override async negotiate(
    expectedClientCode: string,
    signal: AbortSignal,
  ): Promise<RealtimeLifecycleResult<RealtimeGrant>> {
    const api = new RealtimeNegotiateApi(new Configuration({
      basePath: this.basePath,
      fetchApi: this.fetchApi,
      accessToken: () => this.accessTokenProvider.getAccessToken(signal),
    }));

    let response: unknown;
    try {
      response = await api.negotiateRealtimeConnection({ signal });
    } catch (error: unknown) {
      if (signal.aborted) throw error;
      if (error instanceof AccessTokenUnavailableError) {
        return { ok: false, disposition: 'terminal', reason: 'unauthorized' };
      }
      if (error instanceof ResponseError) {
        return classifyStatus(error.response.status);
      }
      // A 2xx with an empty/malformed body is a broken server contract, not a
      // transport failure: report the terminal outcome instead of retrying.
      if (error instanceof SyntaxError) {
        return { ok: false, disposition: 'terminal', reason: 'invalid-contract' };
      }
      return { ok: false, disposition: 'transient', reason: 'network' };
    }

    return validateGrant(response, expectedClientCode, this.now());
  }
}

function operationBasePath(signalRUrl: string): string {
  const url = new URL(signalRUrl);
  const operationPath = '/api/v1/realtime/negotiate';
  if (
    url.username !== ''
    || url.password !== ''
    || url.search !== ''
    || url.hash !== ''
    || !url.pathname.endsWith(operationPath)
  ) {
    throw new Error('Invalid realtime negotiate operation URL.');
  }
  const basePath = url.pathname.slice(0, -operationPath.length).replace(/\/$/, '');
  return `${url.origin}${basePath}`;
}

function classifyStatus(status: number): RealtimeLifecycleResult<never> {
  if (status === 401) return { ok: false, disposition: 'terminal', reason: 'unauthorized' };
  if (status === 403) return { ok: false, disposition: 'terminal', reason: 'forbidden' };
  if (status === 404) return { ok: false, disposition: 'terminal', reason: 'unavailable' };
  if (status === 503 || status === 429 || status >= 500) {
    return { ok: false, disposition: 'transient', reason: 'unavailable' };
  }
  return { ok: false, disposition: 'terminal', reason: 'invalid-contract' };
}

function validateGrant(
  value: unknown,
  expectedClientCode: string,
  now: number,
): RealtimeLifecycleResult<RealtimeGrant> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return invalidGrant();
  const candidate = value as Record<string, unknown>;
  const expiresAtUtc = candidate['expiresAtUtc'] instanceof Date
    ? candidate['expiresAtUtc'].getTime()
    : Number.NaN;

  if (
    !isAllowedConnectionUrl(candidate['url'])
    || !isNonEmptyString(candidate['accessToken'])
    || !Number.isFinite(expiresAtUtc)
    || expiresAtUtc <= now
    || candidate['clientCode'] !== expectedClientCode
    || candidate['hub'] !== 'tiles'
    || candidate['schemaVersion'] !== '1.0'
  ) {
    return invalidGrant();
  }

  return {
    ok: true,
    value: {
      url: candidate['url'],
      accessToken: candidate['accessToken'],
      expiresAtUtc,
      clientCode: candidate['clientCode'],
      hub: 'tiles',
      schemaVersion: '1.0',
    },
  };
}

function invalidGrant(): RealtimeLifecycleResult<never> {
  return { ok: false, disposition: 'terminal', reason: 'invalid-contract' };
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isLoopbackHostname(hostname: string): boolean {
  // Kept in lockstep with the runtime config validator's loopback rule
  // (app-config.validator.ts) — a supported http://[::1] dev deployment must
  // not negotiate successfully and then reject its own valid grant.
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

function isAllowedConnectionUrl(value: unknown): value is string {
  if (!isNonEmptyString(value)) return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:'
      || (url.protocol === 'http:' && isLoopbackHostname(url.hostname));
  } catch {
    return false;
  }
}